// v4 ticket T4 (spec-v4-review.md T4, spec-v4-extras.md A6): the timeline under the review player. Scenes, voice
// lines, music and sounds as lanes, the notes as pins (close ones grouped with a count), the playhead and the In/Out
// range. A tap seeks; a mouse drag picks a range for a note (desktop); a finger drag scrubs (phone). A tap on a
// voice line, the music or a sound starts a note on that sound. Phones show the scenes and pins; "Lanes" opens the rest.
import { useEffect, useMemo, useRef, useState } from "react";
import { fmtTime } from "../../timeline";
import { followScroll, pct, pinGroups, span, stackRows, statusChip, type Note, type Timeline, type TlLine, type TlMusic, type TlSfx } from "../../notes";

export type SoundPick = { kind: "line"; line: TlLine } | { kind: "music"; music: TlMusic } | { kind: "sfx"; sfx: TlSfx };

const ZOOMS = [1, 2, 4, 8] as const;

export function Lanes({
  tl,
  notes,
  t,
  range,
  selected,
  full,
  onSeek,
  onRange,
  onSound,
  onPin,
}: {
  tl: Timeline;
  notes: Note[];
  t: number;
  /** the note being written: its In and Out */
  range: { start: number; end: number | null } | null;
  selected: string | null;
  /** every lane (desktop, or "Lanes" on a phone) */
  full: boolean;
  onSeek: (t: number) => void;
  onRange: (start: number, end: number) => void;
  onSound: (s: SoundPick) => void;
  onPin: (n: Note) => void;
}) {
  const dur = tl.durationS || 1;
  const [zoom, setZoom] = useState<(typeof ZOOMS)[number]>(1);
  const scroller = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const [trackW, setTrackW] = useState(0);
  const [drag, setDrag] = useState<{ x0: number; t0: number; t1: number; mouse: boolean; moved: boolean } | null>(null);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => setTrackW(el.clientWidth)) : null;
    ro?.observe(el);
    setTrackW(el.clientWidth);
    return () => ro?.disconnect();
  }, [zoom, full]);

  // zoomed in: keep the playhead in view
  useEffect(() => {
    const sc = scroller.current;
    if (!sc || zoom === 1) return;
    const next = followScroll(pct(t, dur), sc.clientWidth, sc.scrollWidth, sc.scrollLeft);
    if (next !== sc.scrollLeft) sc.scrollLeft = next;
  }, [t, zoom, dur]);

  const lineRows = useMemo(() => stackRows(tl.lines), [tl.lines]);
  // one voice: no need to name it on every line
  const oneVoice = useMemo(() => new Set(tl.lines.map((l) => l.speakerId)).size <= 1, [tl.lines]);
  const pins = useMemo(() => pinGroups(notes, dur, trackW || 300, 18), [notes, dur, trackW]);
  const ticks = useMemo(() => {
    const step = dur / zoom > 120 ? 30 : dur / zoom > 40 ? 10 : dur / zoom > 15 ? 5 : 1;
    const out: number[] = [];
    for (let s = 0; s <= dur + 1e-6; s += step) out.push(s);
    return out;
  }, [dur, zoom]);

  const timeAt = (clientX: number) => {
    const r = track.current!.getBoundingClientRect();
    return Math.max(0, Math.min(dur, ((clientX - r.left) / (r.width || 1)) * dur));
  };

  const sel = drag?.mouse && drag.moved ? { start: Math.min(drag.t0, drag.t1), end: Math.max(drag.t0, drag.t1) } : range && range.end !== null ? { start: range.start, end: range.end } : null;

  return (
    <div className={`rv-lanes${full ? " is-full" : ""}`}>
      {full && (
        <div className="rv-lanes-head">
          <span className="rv-zoom" role="group" aria-label="Zoom">
            {ZOOMS.map((z) => (
              <button key={z} type="button" className={z === zoom ? "is-on" : ""} aria-pressed={z === zoom} onClick={() => setZoom(z)}>
                {z}x
              </button>
            ))}
          </span>
          <span className="rv-keys" aria-hidden>
            Space play · ←/→ frame · I/O in, out · N note
          </span>
        </div>
      )}
      <div className="rv-lanes-body">
        {full && (
          <ul className="rv-lane-names" aria-hidden>
            <li>Scenes</li>
            <li style={{ height: `${Math.max(1, lineRows.count) * 22 + 6}px` }}>Voice</li>
            <li>Music</li>
            <li>Sounds</li>
            <li>Notes</li>
          </ul>
        )}
        <div className="rv-scroll" ref={scroller}>
          <div
            className="rv-track"
            ref={track}
            style={{ width: `${zoom * 100}%` }}
            onPointerDown={(e) => {
              if ((e.target as HTMLElement).closest("button")) return;
              const at = timeAt(e.clientX);
              e.currentTarget.setPointerCapture(e.pointerId);
              setDrag({ x0: e.clientX, t0: at, t1: at, mouse: e.pointerType === "mouse", moved: false });
              if (e.pointerType !== "mouse") onSeek(at);
            }}
            onPointerMove={(e) => {
              if (!drag) return;
              const at = timeAt(e.clientX);
              const moved = drag.moved || Math.abs(e.clientX - drag.x0) > 4;
              setDrag({ ...drag, t1: at, moved });
              if (!drag.mouse) onSeek(at);
            }}
            onPointerUp={(e) => {
              if (!drag) return;
              const at = timeAt(e.clientX);
              if (drag.mouse && drag.moved && Math.abs(at - drag.t0) > 0.1) onRange(Math.min(drag.t0, at), Math.max(drag.t0, at));
              else onSeek(at);
              setDrag(null);
            }}
            onPointerCancel={() => setDrag(null)}
          >
            {full && (
              <div className="rv-ruler" aria-hidden>
                {ticks.map((s) => (
                  <span key={s} style={{ left: `${pct(s, dur)}%` }}>
                    {fmtTime(s).replace(/\.\d$/, "")}
                  </span>
                ))}
              </div>
            )}
            <div className="rv-lane rv-lane-scenes">
              {tl.scenes.map((sc) => {
                const sp = span(sc.startS, sc.endS, dur);
                return (
                  <button data-action="review.seek" key={sc.n} type="button" className={`rv-scene${t >= sc.startS && t < sc.endS ? " is-now" : ""}`} style={{ left: `${sp.left}%`, width: `${sp.width}%` }} onClick={() => onSeek(sc.startS)} title={`Scene ${sc.n}${sc.onScreen ? `: ${sc.onScreen}` : ""}`} aria-label={`Scene ${sc.n}, at ${fmtTime(sc.startS)}`}>
                    <b>{sc.n}</b>
                    {full && <span>{sc.onScreen || sc.visual}</span>}
                  </button>
                );
              })}
            </div>
            {full && (
              <>
                <div className="rv-lane rv-lane-voice" style={{ height: `${Math.max(1, lineRows.count) * 22 + 6}px` }}>
                  {tl.lines.map((l, i) => {
                    const sp = span(l.startS, l.endS, dur);
                    return (
                      <button key={l.id} type="button" className="rv-item rv-voice" style={{ left: `${sp.left}%`, width: `${sp.width}%`, top: `${3 + (lineRows.rows[i] ?? 0) * 22}px` }} onClick={() => onSound({ kind: "line", line: l })} title={`${l.speaker}: ${l.text}`} aria-label={`Note on ${l.speaker}'s line "${l.text}"`}>
                        <span>
                          {!oneVoice && <b>{l.speaker}</b>} {l.text}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="rv-lane rv-lane-music">
                  {tl.music.map((m, i) => {
                    const sp = span(m.startS, m.endS, dur);
                    return (
                      <button key={i} type="button" className="rv-item rv-music" style={{ left: `${sp.left}%`, width: `${sp.width}%` }} onClick={() => onSound({ kind: "music", music: m })} aria-label={`Note on the music, ${m.label}`}>
                        <span>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="rv-lane rv-lane-sfx">
                  {tl.sfx.map((x, i) => {
                    const sp = span(x.atS, x.endS ?? x.atS + 0.6, dur, 0.9);
                    return (
                      <button key={i} type="button" className={`rv-item rv-sfx${x.kind === "ambience" ? " is-amb" : ""}`} style={{ left: `${sp.left}%`, width: `${sp.width}%` }} onClick={() => onSound({ kind: "sfx", sfx: x })} title={x.label} aria-label={`Note on the ${x.label} sound at ${fmtTime(x.atS)}`}>
                        <span>{x.label}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <div className="rv-lane rv-lane-notes">
              {full &&
                notes
                  .filter((n) => !n.general && n.startS !== null && n.endS !== null && n.endS - n.startS > 0.2)
                  .map((n) => {
                    const sp = span(n.startS!, n.endS!, dur);
                    return <span key={`r${n.id}`} className={`rv-note-range tone-${statusChip(n).tone}`} style={{ left: `${sp.left}%`, width: `${sp.width}%` }} aria-hidden />;
                  })}
              {pins.map((g) => {
                const first = g.notes[0]!;
                const on = g.notes.some((n) => n.id === selected);
                return (
                  <button key={g.key} type="button" className={`rv-pin tone-${statusChip(first).tone}${on ? " is-on" : ""}${first.kind === "like" ? " is-like" : ""}`} style={{ left: `${pct(g.atS, dur)}%` }} onClick={() => onPin(on && g.notes.length > 1 ? (g.notes[(g.notes.findIndex((n) => n.id === selected) + 1) % g.notes.length] ?? first) : first)} aria-label={g.notes.length > 1 ? `${g.notes.length} notes at ${fmtTime(g.atS)}` : `Note at ${fmtTime(g.atS)}: ${first.body || "no words"}`}>
                    {g.notes.length > 1 ? g.notes.length : ""}
                  </button>
                );
              })}
            </div>
            {sel && <span className="rv-sel" style={{ left: `${pct(sel.start, dur)}%`, width: `${Math.max(0.3, pct(sel.end, dur) - pct(sel.start, dur))}%` }} aria-hidden />}
            {range && <span className="rv-in" style={{ left: `${pct(range.start, dur)}%` }} aria-hidden />}
            <span className="rv-head" style={{ left: `${pct(t, dur)}%` }} aria-hidden />
          </div>
        </div>
      </div>
    </div>
  );
}
